import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch } from '../utils/Utils.js';
import '../charts/reproduction/NivoChartCard.css';

// At/above industry average vs. below it is STATE relative to a fixed
// benchmark, not identity — status palette (same good/critical steps used
// for gain/loss in FinanceCharts.js and reproduction outcomes).
const GOOD = '#0ca30c';
const CRITICAL = '#d03b3b';
const REFERENCE_LINE = '#dd6b20';
const INDUSTRY_AVG = 52; // reference line for typical carcass yield %

export const SlaughterChart = () => {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);

  useEffect(() => {
    Fetch('api/data/weight.slaughter', 'GET', null,
      (d) => setRecords((d || []).filter(r => r.yield_pct > 0).sort((a, b) => a.date?.localeCompare(b.date))),
      () => {}
    );
  }, []);

  if (records.length === 0) {
    return (
      <div className="Chart NivoCard">
        <h3>{t('slaughter_yield_chart_title')}</h3>
        <p className="nivo-card-empty">{t('no_slaughter_records')}</p>
      </div>
    );
  }

  const yields = records.map(r => r.yield_pct);
  const avg = Math.round((yields.reduce((a, b) => a + b, 0) / yields.length) * 10) / 10;
  const best = Math.max(...yields);

  // A lot can be slaughtered in multiple batches over time, so indexBy needs
  // a unique key per bar (Nivo, unlike the original Chart.js labels array,
  // collapses same-valued index entries into one bar) — the lot code is
  // still what's actually shown, via an id-to-label lookup on the axis.
  const bars = records.map((r, i) => ({
    id: `${r.lot_code || '—'}::${i}`,
    lot: r.lot_code || '—',
    yield: r.yield_pct,
    abattoir: r.abattoir,
    animalCount: r.animal_count,
    date: r.date,
  }));
  const lotLabelById = Object.fromEntries(bars.map(b => [b.id, b.lot]));

  const yMin = Math.max(0, Math.floor(Math.min(...yields) - 5));
  const yMax = Math.min(100, Math.ceil(Math.max(...yields) + 5));

  return (
    <div className="Chart NivoCard">
      <h3>{t('slaughter_yield_chart_title')}</h3>
      <p className="nivo-card-subtitle">{t('slaughter_yield_chart_sub')}</p>

      <div style={{ height: 300 }}>
        <ResponsiveBar
          data={bars}
          keys={['yield']}
          indexBy="id"
          valueScale={{ type: 'linear', min: yMin, max: yMax }}
          margin={{ top: 12, right: 16, bottom: 42, left: 52 }}
          padding={0.35}
          borderRadius={4}
          colors={(bar) => (bar.data.yield >= INDUSTRY_AVG ? GOOD : CRITICAL)}
          axisBottom={{ tickSize: 0, tickPadding: 8, legend: t('col_lot_code'), legendPosition: 'middle', legendOffset: 34, format: (v) => lotLabelById[v] ?? v }}
          axisLeft={{ tickSize: 0, tickPadding: 8, legend: t('col_yield_pct'), legendPosition: 'middle', legendOffset: -44, format: (v) => `${v}%` }}
          markers={[{
            axis: 'y',
            value: INDUSTRY_AVG,
            legend: `${t('industry_avg')} ${INDUSTRY_AVG}%`,
            legendPosition: 'top-right',
            lineStyle: { stroke: REFERENCE_LINE, strokeWidth: 1.5, strokeDasharray: '5 4' },
            textStyle: { fill: REFERENCE_LINE, fontSize: 10, fontWeight: 700 },
          }]}
          label={(d) => `${d.value.toFixed(1)}%`}
          labelSkipHeight={16}
          labelTextColor="#ffffff"
          enableGridY={true}
          motionConfig="gentle"
          theme={{
            axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
            grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
            labels: { text: { fontWeight: 700, fontSize: 12 } },
          }}
          tooltip={({ data }) => (
            <div className="nivo-tooltip">
              <span className="nivo-tooltip-swatch" style={{ background: data.yield >= INDUSTRY_AVG ? GOOD : CRITICAL }} />
              <strong>{data.lot}</strong>: <span className="nivo-tooltip-value">{data.yield.toFixed(1)}%</span>
              {(data.abattoir || data.animalCount || data.date) && (
                <span style={{ opacity: 0.75 }}>
                  {' — '}
                  {[data.abattoir, data.animalCount ? `${data.animalCount} ${t('col_animal_count').toLowerCase()}` : null, data.date?.slice(0, 10)]
                    .filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          )}
        />
      </div>

      <ul className="nivo-legend-row">
        <li>{t('avg_yield')}: <strong>{avg}%</strong></li>
        <li>{t('best_yield')}: <strong>{best.toFixed(1)}%</strong></li>
        <li>{t('slaughter_history')}: <strong>{records.length}</strong></li>
      </ul>
    </div>
  );
};

export default SlaughterChart;
